import { useState, useEffect } from 'react';
import { getSignedUrl, parseS3Url } from './awsConfig';

interface UseSignedImageResult {
  signedUrl: string | null;
  loading: boolean;
  error: string | null;
}

/**
 * Custom hook to generate and cache signed URLs for private S3 images
 */
export const useSignedImage = (s3Url: string | undefined): UseSignedImageResult => {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!s3Url) {
      setSignedUrl(null);
      setLoading(false);
      setError(null);
      return;
    }

    const generateSignedUrl = async () => {
      setLoading(true);
      setError(null);

      try {
        const { bucket, key } = parseS3Url(s3Url);
        const signed = await getSignedUrl(key, bucket, 3600);
        setSignedUrl(signed);
      } catch (err) {
        console.error('Error generating signed URL:', err);
        setError(err instanceof Error ? err.message : 'Failed to load image');
        setSignedUrl(null);
      } finally {
        setLoading(false);
      }
    };

    generateSignedUrl();
  }, [s3Url]);

  return { signedUrl, loading, error };
};

/**
 * Hook for multiple images - returns a map of original URLs to signed URLs
 */
export const useSignedImages = (s3Urls: (string | undefined)[]): Record<string, UseSignedImageResult> => {
  const [results, setResults] = useState<Record<string, UseSignedImageResult>>({});

  useEffect(() => {
    const validUrls = s3Urls.filter((url): url is string => Boolean(url));
    
    if (validUrls.length === 0) {
      setResults({});
      return;
    }

    const generateAllSignedUrls = async () => {
      const newResults: Record<string, UseSignedImageResult> = {};

      // Initialize all as loading
      validUrls.forEach(url => {
        newResults[url] = { signedUrl: null, loading: true, error: null };
      });
      setResults(newResults);

      // Generate signed URLs for each
      await Promise.all(
        validUrls.map(async (url) => {
          try {
            const { bucket, key } = parseS3Url(url);
            const signed = await getSignedUrl(key, bucket, 3600);
            newResults[url] = { signedUrl: signed, loading: false, error: null };
          } catch (err) {
            console.error(`Error generating signed URL for ${url}:`, err);
            newResults[url] = { 
              signedUrl: null, 
              loading: false, 
              error: err instanceof Error ? err.message : 'Failed to load image' 
            };
          }
        })
      );

      setResults({ ...newResults });
    };

    generateAllSignedUrls();
  }, [JSON.stringify(s3Urls)]); // Use JSON.stringify for deep comparison

  return results;
};